import { AccessibleIcon } from "@radix-ui/react-accessible-icon"
import clsx from "clsx"
import * as React from "react"

import clsxm from "@/lib/clsxm"

import Icon from "@/components/common/Icon/Icon"
import { PrimaryLink } from "@/components/common/Links/PrimaryLink"
import UnstyledLink from "@/components/common/Links/UnstyledLink"

import FacebookLogo from "../../../../public/svg/social-media/facebook.svg"
import InstagramLogo from "../../../../public/svg/social-media/instagram.svg"
import LinkedInLogo from "../../../../public/svg/social-media/linked-in.svg"

type FooterProps = {
  className?: string
} & React.ComponentPropsWithoutRef<`footer`>

export default function Footer({ className, ...rest }: FooterProps) {
  return (
    <footer
      {...rest}
      className={clsxm(`relative z-10 bg-primary-50`, className)}
    >
      <div className={`flex flex-col items-center`}>
        <div
          className={clsx(
            `flex w-full max-w-7xl flex-wrap items-center justify-center space-x-4 px-4 py-3`,
            `sm:justify-between`
          )}
        >
          <div
            className={clsx(
              `order-2 flex basis-full flex-col items-center justify-center space-x-4`,
              `sm:order-1 sm:basis-auto sm:flex-row sm:items-start sm:justify-start`
            )}
          >
            <span className={`order-4 sm:order-1`}>
              &copy; {new Date().getFullYear()} WellNow. All rights reserved.
            </span>
            <PrimaryLink className={`order-2`} href={`/privacy-policy`}>
              Privacy Policy
            </PrimaryLink>
            <PrimaryLink className={`order-3`} href={`/terms-of-service`}>
              Terms of Service
            </PrimaryLink>
          </div>
          <div
            className={clsx(
              `order-1 mb-4 flex space-x-3 text-white`,
              `sm:order-2 sm:mb-0`
            )}
          >
            <SocialIcon
              url={`https://www.facebook.com/WellNowNZ`}
              urlLabel={`Visit the WellNow facebook page`}
              iconAriaLabel={`Facebook logo`}
              icon={FacebookLogo}
            />
            <SocialIcon
              url={`https://www.linkedin.com/company/wellnownz/`}
              urlLabel={`Visit the WellNow linkedin page`}
              iconAriaLabel={`LinkedIn logo`}
              icon={LinkedInLogo}
            />
            <SocialIcon
              url={`https://www.instagram.com/wellnownz/`}
              urlLabel={`Visit the WellNow instagram page`}
              iconAriaLabel={`Instagram logo`}
              icon={InstagramLogo}
            />
          </div>
        </div>
        <div
          className={`w-full max-w-7xl px-4 pb-2 text-right text-xs text-gray-500`}
        >
          Location search by{` `}
          <UnstyledLink
            href={`https://locationiq.com/?ref=link-back`}
            openNewTab
            className={`underline hover:text-gray-600`}
          >
            LocationIQ.com
          </UnstyledLink>
        </div>
      </div>
    </footer>
  )
}

type SocialIconProps = {
  iconAriaLabel: string
  urlLabel: string
  url: string
  icon: React.ElementType
}

function SocialIcon({ iconAriaLabel, icon, urlLabel, url }: SocialIconProps) {
  return (
    <UnstyledLink aria-label={urlLabel} openNewTab href={url}>
      <div
        className={clsx(
          `rounded-md bg-gray-900 p-2 transition-colors`,
          `hover:bg-gray-800`
        )}
      >
        <AccessibleIcon label={iconAriaLabel}>
          <Icon icon={icon} />
        </AccessibleIcon>
      </div>
    </UnstyledLink>
  )
}
